import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiFetch, apiFetchBlob } from '@/domains/general/api/client';
import { paginationParams } from '@/domains/general/api/constants';

import { isIndexingTerminal } from '../helpers/indexingStatus';

import { documentKeys } from './keys';
import type { DocumentListItem, DocumentListResponse } from './types';

// Zolang er documenten in de wachtrij of in verwerking zitten, halen we de lijst periodiek
// opnieuw op zodat de status live doorloopt (gepland → wordt verwerkt → verwerkt).
const INDEXING_POLL_INTERVAL_MS = 3_000;

export const documentQueries = {
  list: (caseId: string) =>
    queryOptions({
      queryKey: documentKeys.list(caseId),
      queryFn: (): Promise<DocumentListResponse> =>
        apiFetch(`/cases/${caseId}/documents?${paginationParams()}`),
      enabled: !!caseId,
      refetchInterval: (query) =>
        query.state.data?.items.some((d) => !isIndexingTerminal(d.indexingStatus))
          ? INDEXING_POLL_INTERVAL_MS
          : false,
    }),
  detail: (caseId: string, id: string) =>
    queryOptions({
      queryKey: documentKeys.detail(caseId, id),
      queryFn: (): Promise<DocumentListItem> =>
        apiFetch(`/cases/${caseId}/documents/${id}`),
      enabled: !!(caseId && id),
    }),
  blob: (caseId: string, id: string) =>
    queryOptions({
      queryKey: [...documentKeys.detail(caseId, id), 'blob'] as const,
      queryFn: (): Promise<Blob> =>
        apiFetchBlob(`/cases/${caseId}/documents/${id}/download`),
      enabled: !!(caseId && id),
      staleTime: 5 * 60 * 1000,
      retry: false,
    }),
};

export function useDocuments(caseId: string) {
  return useQuery(documentQueries.list(caseId));
}

export function useDocument(caseId: string, id: string) {
  return useQuery(documentQueries.detail(caseId, id));
}

export function useDocumentBlob(caseId: string, id: string) {
  return useQuery(documentQueries.blob(caseId, id));
}
